/* Image readiness and lookahead for the first home slideshow only. */
(function () {
  'use strict';
  var installation = Symbol.for('amai.homeCarousel');
  if (document[installation]) return;
  document[installation] = true;
  var controllers = new Map();

  function HomeCarousel(root, flickity) {
    this.root = root;
    this.flickity = flickity;
    this.slides = Array.from(root.querySelectorAll('.slideshow__slide'));
    this.records = this.slides.map(function (slide) {
      return { image: slide.querySelector('picture img'), failed: {}, ready: null, pending: null };
    });
    this.media = window.matchMedia('(max-width: 589px)');
    this.originals = {};
    this.listeners = [];
    this.frames = new Set();
    this.timer = null;
    this.queue = [];
    this.warming = false;
    this.destroyed = false;
    this.visibleReady = false;
    this.intent = 0;
    this.pendingIndex = null;
    this.paused = root.dataset.homeAutoplay !== 'true';
    this.interval = Number(root.dataset.speed) || 6000;
    this.bind();
  }

  HomeCarousel.prototype.mode = function () { return this.media.matches ? 'mobile' : 'desktop'; };
  HomeCarousel.prototype.index = function (index) {
    return ((Number(index) % this.slides.length) + this.slides.length) % this.slides.length;
  };
  HomeCarousel.prototype.listen = function (target, name, callback) {
    target.addEventListener(name, callback);
    this.listeners.push(function () { target.removeEventListener(name, callback); });
  };
  HomeCarousel.prototype.frame = function (callback) {
    var self = this;
    var id = window.requestAnimationFrame(function () {
      self.frames.delete(id);
      if (!self.destroyed) callback();
    });
    this.frames.add(id);
  };
  HomeCarousel.prototype.afterPaint = function (callback) {
    var self = this;
    this.frame(function () { self.frame(callback); });
  };

  HomeCarousel.prototype.ensure = function (index, retry) {
    var self = this, record = this.records[index], mode = this.mode();
    if (!record || !record.image) return Promise.resolve();
    if (retry) delete record.failed[mode];
    if (record.failed[mode]) return Promise.reject(new Error('Image unavailable'));
    if (record.ready === mode && record.image.complete && record.image.naturalWidth) return Promise.resolve();
    if (record.pending && record.pending.mode === mode) return record.pending.promise;
    if (record.pending) record.pending.cancel();

    var image = record.image, pending = { mode: mode };
    record.pending = pending;
    pending.promise = new Promise(function (resolve, reject) {
      var finished = false, decoding = false;
      function cleanup() {
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
        if (record.pending === pending) record.pending = null;
      }
      function finish(error) {
        if (finished) return;
        finished = true;
        cleanup();
        if (error) reject(error);
        else {
          record.ready = mode;
          image.classList.add('lazyloaded');
          resolve();
        }
      }
      function failed() {
        record.failed[mode] = true;
        finish(new Error('Image failed to load'));
      }
      function loaded() {
        if (finished || decoding || !image.complete || !image.naturalWidth) return;
        decoding = true;
        var decode = image.decode ? image.decode() : Promise.resolve();
        decode.then(function () {
          if (self.destroyed || mode !== self.mode()) finish(new Error('Image superseded'));
          else finish();
        }, function () {
          if (!finished) failed();
        });
      }
      pending.cancel = function () { finish(new Error('Image superseded')); };
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      // Set picture sources before the fallback so mobile never starts the desktop download.
      image.parentElement.querySelectorAll('source[data-home-srcset]').forEach(function (source) {
        source.setAttribute('srcset', source.getAttribute('data-home-srcset'));
      });
      if (image.hasAttribute('data-home-srcset')) image.setAttribute('srcset', image.getAttribute('data-home-srcset'));
      if (image.hasAttribute('data-home-src')) image.setAttribute('src', image.getAttribute('data-home-src'));
      loaded();
    });
    return pending.promise;
  };

  HomeCarousel.prototype.clearTimer = function () {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
  };
  HomeCarousel.prototype.schedule = function () {
    var self = this;
    this.clearTimer();
    if (this.destroyed || this.paused || !this.visibleReady || this.pendingIndex !== null ||
        document.visibilityState === 'hidden' || this.slides.length < 2) return;
    this.timer = window.setTimeout(function () {
      self.timer = null;
      if (self.destroyed || self.paused || document.visibilityState === 'hidden') return;
      for (var step = 1; step < self.slides.length; step++) {
        var next = self.index(self.flickity.selectedIndex + step);
        if (!self.records[next].failed[self.mode()]) {
          self.request(next, false);
          return;
        }
      }
    }, this.interval);
  };

  HomeCarousel.prototype.warm = function () {
    var self = this, selected = this.flickity.selectedIndex;
    this.queue = [this.index(selected + 1), this.index(selected - 1)].filter(function (index, pos, list) {
      return index !== selected && list.indexOf(index) === pos && !self.records[index].failed[self.mode()];
    });
    this.drain();
  };
  HomeCarousel.prototype.drain = async function () {
    if (this.warming || this.destroyed || !this.visibleReady || this.pendingIndex !== null) return;
    this.warming = true;
    while (!this.destroyed && this.pendingIndex === null && this.queue.length) {
      var index = this.queue.shift();
      try { await this.ensure(index, false); } catch (_) { /* Retry only on manual intent. */ }
    }
    this.warming = false;
  };

  HomeCarousel.prototype.request = function (index, manual, wrap, instant) {
    var self = this, target = this.index(index);
    if (this.destroyed || !Number.isFinite(target)) return;
    var intent = ++this.intent;
    this.clearTimer();
    this.pendingIndex = target;
    this.ensure(target, manual).then(function () {
      if (self.destroyed || intent !== self.intent) return;
      self.pendingIndex = null;
      self.visibleReady = true;
      self.originals.select.call(self.flickity, target, wrap, instant);
      self.afterPaint(function () {
        if (intent !== self.intent) return;
        self.schedule();
        self.warm();
      });
    }, function () {
      if (self.destroyed || intent !== self.intent) return;
      self.pendingIndex = null;
      self.schedule();
      self.warm();
    });
  };

  HomeCarousel.prototype.bind = function () {
    var self = this, flickity = this.flickity;
    ['select', 'next', 'previous', 'playPlayer', 'pausePlayer', 'stopPlayer'].forEach(function (name) {
      self.originals[name] = flickity[name];
    });
    // Keep Flickity's geometry, controls and fades; gate selection on this instance only.
    this.originals.stopPlayer.call(flickity);
    flickity.select = function (index, wrap, instant) { self.request(index, true, wrap, instant); };
    flickity.next = function (wrap, instant) {
      self.request((self.pendingIndex === null ? flickity.selectedIndex : self.pendingIndex) + 1, true, wrap, instant);
    };
    flickity.previous = function (wrap, instant) {
      self.request((self.pendingIndex === null ? flickity.selectedIndex : self.pendingIndex) - 1, true, wrap, instant);
    };
    flickity.playPlayer = function () { self.paused = false; self.schedule(); };
    flickity.pausePlayer = flickity.stopPlayer = function () { self.paused = true; self.clearTimer(); };
    this.onManual = function () { self.paused = true; self.clearTimer(); };
    this.onDestroy = function () { self.destroy(); };
    flickity.on('uiChange', this.onManual);
    flickity.on('pointerDown', this.onManual);
    flickity.on('destroy', this.onDestroy);
    // Native autoplay is disabled at boot, so Motion does not bind its pause button.
    var wrapper = this.root.closest('.slideshow-wrapper');
    var pauseButton = wrapper && wrapper.querySelector('.slideshow__pause');
    if (pauseButton && this.root.dataset.homeAutoplay === 'true') {
      this.listen(pauseButton, 'click', function () {
        if (pauseButton.classList.contains('is-paused')) {
          pauseButton.classList.remove('is-paused');
          flickity.playPlayer();
        } else {
          pauseButton.classList.add('is-paused');
          flickity.pausePlayer();
        }
      });
    }
    this.listen(document, 'visibilitychange', function () { self.schedule(); });
    this.onBreakpoint = function () {
      ++self.intent;
      self.pendingIndex = null;
      self.visibleReady = false;
      self.queue = [];
      self.clearTimer();
      self.records.forEach(function (record) {
        record.ready = null;
        if (record.pending) record.pending.cancel();
      });
      // Let <picture> select its new source before checking cached readiness.
      self.afterPaint(function () { self.request(flickity.selectedIndex, false, true, true); });
    };
    this.media.addEventListener('change', this.onBreakpoint);
    this.root.setAttribute('data-home-carousel-ready', '');
    this.request(flickity.selectedIndex, false, true, true);
  };

  HomeCarousel.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    ++this.intent;
    this.clearTimer();
    this.frames.forEach(function (id) { window.cancelAnimationFrame(id); });
    this.frames.clear();
    this.records.forEach(function (record) { if (record.pending) record.pending.cancel(); });
    this.listeners.forEach(function (remove) { remove(); });
    this.media.removeEventListener('change', this.onBreakpoint);
    this.root.removeAttribute('data-home-carousel-ready');
    this.flickity.off('uiChange', this.onManual);
    this.flickity.off('pointerDown', this.onManual);
    this.flickity.off('destroy', this.onDestroy);
    var flickity = this.flickity;
    Object.keys(this.originals).forEach(function (name) { flickity[name] = this.originals[name]; }, this);
    controllers.delete(this.root);
  };

  function scan() {
    if (!window.Flickity) return;
    document.querySelectorAll('[data-home-carousel]').forEach(function (root) {
      var flickity = window.Flickity.data(root), current = controllers.get(root);
      if (current && current.flickity !== flickity) current.destroy();
      if (flickity && !controllers.has(root)) controllers.set(root, new HomeCarousel(root, flickity));
    });
  }
  document.addEventListener('page:loaded', scan);
  document.addEventListener('shopify:section:load', function () { window.requestAnimationFrame(scan); });
  document.addEventListener('shopify:section:unload', function (event) {
    controllers.forEach(function (controller, root) {
      if (event.target.contains(root)) controller.destroy();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
